import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, Workflow, Bot, Network, Plug, Command, BarChart2, TrendingUp, X, FileText, Box, Code, LayoutDashboard, Waypoints } from 'lucide-react';
import { useUiStore, type ObjectPanelContext } from '../store/uiStore';
import { navigateToResource } from './useResourceRoute';
import { useNavigationStore } from '../store/navigationStore';
import { usePipelineStore } from '../store/pipelineStore';
import { useAgentStore } from '../store/agentStore';
import { useConnectorStore } from '../store/connectorStore';
import { useTranslation } from 'react-i18next';
import { useSearchStore, type SearchResult } from '../store/searchStore';
import { NAV_ITEMS as SIDEBAR_ITEMS } from './GroupedNavRail';
import { useUiV2 } from './uiFlags';
import { permissionModuleFor } from './routes';
import { usePermission } from '../hooks/usePermission';
import type { Pipeline } from '../types/pipeline';
import type { AgentConfig } from '../store/agentStore';
import type { ConnectorConfig } from '../types/connector';

interface Result {
  id: string;
  label: string;
  sublabel?: string;
  category: string;
  icon: React.ReactNode;
  action: () => void;
  /** ui_v2 filter chip this result belongs to (undefined: only under Todo). */
  chip?: ChipId;
  /** ui_v2: Enter opens this in the side panel, Cmd+Enter runs `full`. */
  panel?: ObjectPanelContext;
  full?: () => void;
}

// ui_v2 type chips above the results (C1, REDISENO_UI.md 3.3).
type ChipId = 'all' | 'objects' | 'records' | 'pipelines' | 'functions' | 'apps' | 'commands';
const CHIPS: { id: ChipId; i18nKey: string; label: string }[] = [
  { id: 'all', i18nKey: 'shell.chips.all', label: 'All' },
  { id: 'objects', i18nKey: 'shell.chips.objects', label: 'Objects' },
  { id: 'records', i18nKey: 'shell.chips.records', label: 'Records' },
  { id: 'pipelines', i18nKey: 'shell.chips.pipelines', label: 'Pipelines' },
  { id: 'functions', i18nKey: 'shell.chips.functions', label: 'Functions' },
  { id: 'apps', i18nKey: 'shell.chips.apps', label: 'Apps' },
  { id: 'commands', i18nKey: 'shell.chips.commands', label: 'Commands' },
];
const CHIP_BY_TYPE: Partial<Record<SearchResult['type'], ChipId>> = {
  object_type: 'objects', record: 'records', pipeline: 'pipelines', logic: 'functions', dashboard: 'apps',
};
const PANEL_BY_TYPE: Partial<Record<SearchResult['type'], { panel: ObjectPanelContext['type']; page: string }>> = {
  object_type: { panel: 'objectType', page: 'ontology' },
  pipeline: { panel: 'pipeline', page: 'pipelines' },
  agent: { panel: 'agent', page: 'agents' },
  connector: { panel: 'connector', page: 'connectors' },
};

interface NavCommandItem {
  id: string;
  label: string;
  i18nKey: string;
  icon: React.ReactNode;
  page: string;
  superadminOnly?: boolean;
}

const NAV_ITEMS: NavCommandItem[] = [
  { id: 'connectors',    label: 'Connectors',    i18nKey: 'nav.connectors',    icon: <Plug size={14} />,     page: 'connectors' },
  { id: 'ontology',      label: 'Ontology',       i18nKey: 'nav.ontology',      icon: <Network size={14} />,  page: 'ontology' },
  { id: 'pipelines',     label: 'Pipelines',      i18nKey: 'nav.pipelines',     icon: <Workflow size={14} />, page: 'pipelines' },
  { id: 'agents',        label: 'Agent Studio',   i18nKey: 'nav.agentStudio',   icon: <Bot size={14} />,      page: 'agents' },
  { id: 'human-actions', label: 'Actions Queue',  i18nKey: 'nav.actions',       icon: <Command size={14} />,  page: 'human-actions' },
  { id: 'events',        label: 'Event Log',      i18nKey: 'nav.eventLog',      icon: <Search size={14} />,   page: 'events' },
  { id: 'process',       label: 'Process Mining', i18nKey: 'nav.processMining', icon: <Search size={14} />,   page: 'process' },
  { id: 'logic',         label: 'Logic Studio',   i18nKey: 'nav.logicStudio',   icon: <Search size={14} />,   page: 'logic' },
];

// With the new interface (ui_v2) Cmd+K is the only search: it offers every
// sidebar destination (its own list drifted: Event Log and Process Mining
// pointed at page ids nothing rendered) plus the server search over records
// and resources that used to live in a separate Search modal.
const NAV_ITEMS_V2: NavCommandItem[] = [
  ...SIDEBAR_ITEMS.map(item => ({ id: item.id, label: item.label, i18nKey: item.i18nKey, icon: item.icon, page: item.path, superadminOnly: item.superadminOnly })),
  { id: 'data-analysis', label: 'Data analysis', i18nKey: 'nav.dataAnalysis', icon: <Workflow size={14} />,  page: 'data-analysis' },
  { id: 'data-lineage',  label: 'Lineage',       i18nKey: 'nav.lineage',      icon: <Waypoints size={14} />, page: 'data-lineage' },
  { id: 'academy',       label: 'PAI Academy',   i18nKey: 'nav.academy',      icon: <Search size={14} />,    page: 'academy' },
];

const SEARCH_ICONS: Record<SearchResult['type'], React.ReactNode> = {
  record: <FileText size={14} />, object_type: <Box size={14} />, pipeline: <Workflow size={14} />,
  connector: <Plug size={14} />, agent: <Bot size={14} />, logic: <Code size={14} />, dashboard: <LayoutDashboard size={14} />,
};

const KBD: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 20, height: 18, padding: '0 4px',
    fontSize: 10, fontFamily: 'var(--font-mono)',
    color: 'var(--color-text-subtle)',
    border: '1px solid var(--color-border-emphasis)',
    borderRadius: 3,
    backgroundColor: 'var(--color-base)',
  }}>
    {children}
  </span>
);

export const CommandPalette: React.FC = () => {
  const { t } = useTranslation();
  const { commandPaletteOpen, closeCommandPalette, openObjectPanel, enterWidgetSelection, dashboardComparison, setDashboardComparison } = useUiStore();
  const { navigateTo, navigateToObject } = useNavigationStore();
  const uiV2 = useUiV2();
  const { canAccess, isPlatformOperator } = usePermission();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [chip, setChip] = useState<ChipId>('all');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Gather searchable items from stores
  const pipelines: Pipeline[] = usePipelineStore((s) => s.pipelines);
  const agents: AgentConfig[] = useAgentStore((s) => s.agents);
  const connectors: ConnectorConfig[] = useConnectorStore((s) => s.connectors);

  // Server-side search (ui_v2 only): records, object types and resources.
  const serverResults = useSearchStore((s) => s.results);
  const serverResultsFor = useSearchStore((s) => s.resultsFor);
  const runSearch = useSearchStore((s) => s.search);
  const clearSearch = useSearchStore((s) => s.clear);
  useEffect(() => {
    if (!uiV2 || !commandPaletteOpen) return;
    const q = query.trim();
    if (!q) { clearSearch(); return; }
    const timer = setTimeout(() => { void runSearch(q); }, 200);
    return () => clearTimeout(timer);
  }, [uiV2, query, commandPaletteOpen, runSearch, clearSearch]);

  const buildResults = useCallback((): Result[] => {
    const q = query.toLowerCase().trim();

    const catNavigate = t('shell.categories.navigate', 'Navigate');
    const catPipelines = t('shell.categories.pipelines', 'Pipelines');
    const catAgents = t('shell.categories.agents', 'Agents');
    const catConnectors = t('shell.categories.connectors', 'Connectors');
    const catCompare = t('shell.categories.compare', 'Compare');
    const catActions = t('shell.categories.actions', 'Actions');

    // The new list offers only what the sidebar would show this user.
    const navItems = uiV2
      ? NAV_ITEMS_V2.filter(item => canAccess(permissionModuleFor(item.page)) && (!item.superadminOnly || isPlatformOperator))
      : NAV_ITEMS;
    const nav: Result[] = navItems
      .filter((item) => {
        const itemLabel = t(item.i18nKey, item.label);
        return !q || itemLabel.toLowerCase().includes(q) || item.label.toLowerCase().includes(q) || item.id.includes(q);
      })
      .map((item) => ({
        id: `nav-${item.id}`,
        label: t(item.i18nKey, item.label),
        category: catNavigate,
        chip: 'commands' as ChipId,
        icon: item.icon,
        action: () => { navigateTo(item.page); closeCommandPalette(); },
      }));

    const pipelineResults: Result[] = (pipelines || [])
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .slice(0, 5)
      .map((p) => ({
        id: `pipeline-${p.id}`,
        label: p.name,
        sublabel: t('pipelines.title', 'Pipeline'),
        category: catPipelines,
        chip: 'pipelines' as ChipId,
        icon: <Workflow size={14} />,
        action: () => { navigateTo('pipelines'); closeCommandPalette(); },
      }));

    const agentResults: Result[] = (agents || [])
      .filter((a) => !q || a.name.toLowerCase().includes(q))
      .slice(0, 5)
      .map((a) => ({
        id: `agent-${a.id}`,
        label: a.name,
        sublabel: t('agents.title', 'Agent'),
        category: catAgents,
        icon: <Bot size={14} />,
        action: () => { navigateTo('agents'); closeCommandPalette(); },
      }));

    const connectorResults: Result[] = (connectors || [])
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .slice(0, 5)
      .map((c) => ({
        id: `connector-${c.id}`,
        label: c.name,
        sublabel: (c as ConnectorConfig).type || t('connectors.title', 'Data Source'),
        category: catConnectors,
        icon: <Plug size={14} />,
        action: () => { navigateTo('connectors'); closeCommandPalette(); },
      }));

    // Period comparison — "vs" turns every dashboard metric into value +
    // green/red delta against the previous period.
    const compareEntries: { id: string; label: string; sublabel: string; mode: 'last_week' | 'last_month' }[] = [
      {
        id: 'compare-week',
        label: t('shell.vsLastWeek', 'vs last week'),
        sublabel: t('shell.vsLastWeekDesc', 'Dashboard KPIs: last 7 days vs the 7 before'),
        mode: 'last_week',
      },
      {
        id: 'compare-month',
        label: t('shell.vsLastMonth', 'vs last month'),
        sublabel: t('shell.vsLastMonthDesc', 'Dashboard KPIs: last 30 days vs the 30 before'),
        mode: 'last_month',
      },
    ];
    const compareResults: Result[] = compareEntries
      .filter((c) => !q || c.label.toLowerCase().includes(q) || 'vs versus compare comparison comparar'.includes(q))
      .map((c) => ({
        id: c.id,
        label: c.label,
        sublabel: c.sublabel,
        category: catCompare,
        chip: 'commands' as ChipId,
        icon: <TrendingUp size={14} />,
        action: () => { setDashboardComparison(c.mode); },
      }));
    if (dashboardComparison !== 'none' && (!q || 'clear comparison vs limpiar'.includes(q))) {
      compareResults.push({
        id: 'compare-clear',
        label: t('shell.clearComparison', 'Clear comparison'),
        sublabel: dashboardComparison === 'last_week'
          ? t('shell.vsLastWeekActive', 'vs last week is active')
          : t('shell.vsLastMonthActive', 'vs last month is active'),
        category: catCompare,
        chip: 'commands',
        icon: <X size={14} />,
        action: () => { setDashboardComparison('none'); },
      });
    }

    const actionLabel = t('shell.analyzeWidgets', 'Analyze widgets (correlation)');
    const actionResults: Result[] = (!q || actionLabel.toLowerCase().includes(q) || 'analyze correlation widgets analizar correlacion'.includes(q))
      ? [{
          id: 'action-analyze-widgets',
          label: actionLabel,
          sublabel: t('shell.analyzeWidgetsDesc', 'Select 2-4 dashboard widgets to compare'),
          category: catActions,
          chip: 'commands',
          icon: <BarChart2 size={14} />,
          action: () => { enterWidgetSelection(); },
        }]
      : [];

    const catSearch = t('shell.categories.search', 'Records and resources');
    const searchResults: Result[] = uiV2 && q && serverResultsFor.trim().toLowerCase() === q
      ? serverResults.slice(0, 8).map((r) => ({
          id: `search-${r.type}-${r.id}`,
          label: r.title,
          sublabel: r.subtitle,
          category: catSearch,
          icon: SEARCH_ICONS[r.type] ?? <Search size={14} />,
          chip: CHIP_BY_TYPE[r.type],
          panel: PANEL_BY_TYPE[r.type] ? { type: PANEL_BY_TYPE[r.type]!.panel, id: r.id, label: r.title } : undefined,
          full: PANEL_BY_TYPE[r.type] ? () => { navigateToResource(PANEL_BY_TYPE[r.type]!.page, r.id); closeCommandPalette(); } : undefined,
          action: () => {
            if (r.type === 'record' && r.object_type_id) navigateToObject(r.object_type_id, r.id);
            else navigateTo(r.path);
            closeCommandPalette();
          },
        }))
      : [];

    return [...compareResults, ...actionResults, ...nav, ...searchResults, ...pipelineResults, ...agentResults, ...connectorResults];
  }, [uiV2, canAccess, isPlatformOperator, query, pipelines, agents, connectors, serverResults, serverResultsFor, enterWidgetSelection, dashboardComparison, setDashboardComparison, navigateTo, navigateToObject, closeCommandPalette, t]);

  const allResults = buildResults();
  const results = uiV2 && chip !== 'all' ? allResults.filter((r) => r.chip === chip) : allResults;

  useEffect(() => { setActiveIdx(0); }, [query, chip]);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setChip('all');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!commandPaletteOpen) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const r = results[activeIdx];
        if (!r) return;
        // ui_v2: Enter opens a resource in the side panel, Cmd/Ctrl+Enter on
        // its full page. Pages, records and commands keep their one action.
        if (uiV2 && r.panel && (e.metaKey || e.ctrlKey) && r.full) r.full();
        else if (uiV2 && r.panel) { openObjectPanel(r.panel); closeCommandPalette(); }
        else r.action();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [commandPaletteOpen, results, activeIdx, uiV2, openObjectPanel, closeCommandPalette]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!commandPaletteOpen) return null;

  // Group results by category
  const grouped: Record<string, Result[]> = {};
  for (const r of results) {
    if (!grouped[r.category]) grouped[r.category] = [];
    grouped[r.category].push(r);
  }

  let globalIdx = 0;

  return createPortal(
    <div
      className="cmd-backdrop"
      onClick={closeCommandPalette}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        zIndex: 9000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '15vh',
      }}
    >
      <div
        className="cmd-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560, maxHeight: 440,
          backgroundColor: 'var(--color-surface-elevated, #FFFFFF)',
          border: '1px solid var(--color-border-emphasis)',
          borderRadius: 8,
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Search input */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 16px',
          borderBottom: '1px solid var(--color-border)',
          height: 52, flexShrink: 0,
        }}>
          <Search size={16} style={{ color: 'var(--color-text-subtle)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={uiV2
              ? t('shell.cmdPalettePlaceholderV2', 'Search pages, records and resources')
              : t('shell.cmdPalettePlaceholder', "Search modules, pipelines, agents... or type 'vs' to compare periods")}
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: 14, fontFamily: 'var(--font-interface)',
              backgroundColor: 'transparent',
              color: 'var(--color-text)',
            }}
          />
          <KBD>esc</KBD>
        </div>

        {uiV2 && (
          <div role="tablist" aria-label={t('shell.chips.label', 'Filter by type')} data-testid="cmd-chips" style={{
            display: 'flex', gap: 6, padding: '8px 16px', flexWrap: 'wrap',
            borderBottom: '1px solid var(--color-border)', flexShrink: 0,
          }}>
            {CHIPS.map((c) => {
              const on = chip === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  data-testid={`cmd-chip-${c.id}`}
                  onClick={() => { setChip(c.id); inputRef.current?.focus(); }}
                  style={{
                    height: 22, padding: '0 10px', fontSize: 11, cursor: 'pointer',
                    borderRadius: 11, fontFamily: 'var(--font-interface)',
                    border: `1px solid ${on ? 'var(--color-interactive)' : 'var(--color-border)'}`,
                    backgroundColor: on ? 'var(--color-interactive-dim)' : 'transparent',
                    color: on ? 'var(--color-interactive)' : 'var(--color-text-muted)',
                    fontWeight: on ? 600 : 400,
                  }}
                >
                  {t(c.i18nKey, c.label)}
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div ref={listRef} style={{ flex: 1, overflowY: 'auto' }}>
          {results.length === 0 && (
            <div style={{
              padding: '32px 16px', textAlign: 'center',
              fontSize: 13, color: 'var(--color-text-muted)',
            }}>
              {t('shell.noResultsFor', { query, defaultValue: `No results for "${query}"` })}
            </div>
          )}
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <div style={{
                padding: '8px 16px 4px',
                fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--color-text-subtle)',
                borderTop: '1px solid var(--color-border)',
              }}>
                {category}
              </div>
              {items.map((r) => {
                const idx = globalIdx++;
                const isActive = idx === activeIdx;
                return (
                  <div
                    key={r.id}
                    data-idx={idx}
                    onClick={r.action}
                    onMouseEnter={() => setActiveIdx(idx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 16px', cursor: 'pointer',
                      backgroundColor: isActive ? 'var(--color-interactive-dim)' : 'transparent',
                      transition: 'background-color 60ms',
                    }}
                  >
                    <span style={{
                      color: isActive ? 'var(--color-interactive)' : 'var(--color-text-muted)',
                      flexShrink: 0, lineHeight: 0,
                    }}>
                      {r.icon}
                    </span>
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: 'var(--color-text)' }}>
                      {r.label}
                    </span>
                    {r.sublabel && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-subtle)' }}>
                        {r.sublabel}
                      </span>
                    )}
                    {isActive && <KBD>↵</KBD>}
                    {isActive && uiV2 && r.panel && <><KBD>⌘</KBD><KBD>↵</KBD></>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          height: 36, padding: '0 16px',
          borderTop: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', gap: 12,
          fontSize: 11, color: 'var(--color-text-subtle)',
          flexShrink: 0,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><KBD>↑</KBD><KBD>↓</KBD> {t('common.navigate', 'navigate')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><KBD>↵</KBD> {t('common.select', 'select')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><KBD>esc</KBD> {t('common.close', 'close')}</span>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <KBD>⌘</KBD><KBD>K</KBD>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CommandPalette;
